import { getAuth } from 'firebase/auth';
import { captureAsyncError } from '../../../utils/errorHandler';

// 再読み取りリクエスト型
export interface ReOcrPatternRequest {
  resultId: string;
  patternId: string;
  itemList: Record<string, unknown>[];
  targetItemIds?: string[];
  geminiModel?: number;
  thinkingBudget?: number | null;
  thinkingLevel?: string | null;
  // #2960 再読取モーダルで再設定する検証設定
  structuredEngine?: number;
  includeCoordinates?: boolean;
  geminiTemperature?: number | null;
  geminiTopP?: number | null;
  isReclassification?: boolean;
  /**
   * 子パターンの指定（#2967）。
   * 子パターンID / 'base'（標準で読む）/ 'auto'（AIに再判定させる）。
   * 未指定なら現在適用されている子パターンを維持する
   */
  subPatternId?: string;
  provisionalDeducted?: boolean;
  feProvisionalCreditAmount?: number;
  feProvisionalUsageCount?: number;
  // 仮加算（current_month_ocr_cost）に +15円 が乗ったか。BEの確定 delta 計算に渡す。
  provisionalHasMasterMapping?: boolean;
  isProxy?: boolean;
  operatorId?: string;
}

// 再読み取りレスポンス型
export interface ReOcrPatternResponse {
  success: boolean;
  resultId: string;
  resultData: Record<string, unknown>;
  isOther?: boolean;
  error?: {
    message: string;
    details?: unknown;
  };
}

/**
 * OCR結果画面からの再読み取り処理を実行
 */
export const handleReOcrPatternResult = captureAsyncError(
  async (request: ReOcrPatternRequest): Promise<ReOcrPatternResponse> => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('ユーザー認証が必要です');
      }

      const idToken = await user.getIdToken();
      const functionUrl = process.env.REACT_APP_FUNCTION_URL;

      if (!functionUrl) {
        throw new Error('REACT_APP_FUNCTION_URL environment variable is not set');
      }

      const body: Record<string, unknown> = {
        resultId: request.resultId,
        patternId: request.patternId,
        itemList: request.itemList,
      };

      if (request.targetItemIds && request.targetItemIds.length > 0) {
        body.targetItemIds = request.targetItemIds;
      }
      if (request.geminiModel !== undefined) {
        body.geminiModel = request.geminiModel;
      }
      if (request.thinkingBudget !== undefined && request.thinkingBudget !== null) {
        body.thinkingBudget = request.thinkingBudget;
      }
      if (request.thinkingLevel !== undefined && request.thinkingLevel !== null) {
        body.thinkingLevel = request.thinkingLevel;
      }
      if (request.structuredEngine !== undefined) {
        body.structuredEngine = request.structuredEngine;
      }
      if (request.includeCoordinates) {
        body.includeCoordinates = true;
      }
      if (request.geminiTemperature !== undefined && request.geminiTemperature !== null) {
        body.geminiTemperature = request.geminiTemperature;
      }
      if (request.geminiTopP !== undefined && request.geminiTopP !== null) {
        body.geminiTopP = request.geminiTopP;
      }
      if (request.isReclassification) {
        body.isReclassification = request.isReclassification;
      }
      if (request.subPatternId !== undefined) {
        body.subPatternId = request.subPatternId;
      }
      if (request.provisionalDeducted) {
        body.provisionalDeducted = request.provisionalDeducted;
      }
      if (request.feProvisionalCreditAmount !== undefined) {
        body.feProvisionalCreditAmount = request.feProvisionalCreditAmount;
      }
      if (request.feProvisionalUsageCount !== undefined) {
        body.feProvisionalUsageCount = request.feProvisionalUsageCount;
      }
      if (request.provisionalHasMasterMapping) {
        body.provisionalHasMasterMapping = true;
      }
      if (request.isProxy !== undefined) {
        body.isProxy = request.isProxy;
      }
      if (request.operatorId) {
        body.operatorId = request.operatorId;
      }

      // OCR処理は最大540秒かかるため、タイムアウトを600秒に設定
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000);

      const response = await fetch(`${functionUrl}/reOcrPatternResult`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await response.json().catch(async () => {
          await response.text();
        });
        throw new Error('再読み取り処理に失敗しました。もう一度お試しください。');
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error.message || '再読み取り処理に失敗しました。');
      }

      return result as ReOcrPatternResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('再読み取り処理に失敗しました。もう一度お試しください。');
    }
  },
);
